import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { money, surfaceSx } from "../center/ownerCenterUi";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function mondayIndex(jsDay) {
  return (jsDay + 6) % 7;
}

function cellCurrencySummary(day) {
  const entries = Object.entries(day?.financeByCurrency || {});
  if (!entries.length) return null;
  const [currency, row] = entries[0];
  return { currency, net: Number(row.net || 0), extra: Math.max(0, entries.length - 1) };
}

export default function OwnerMonthGrid({ month, days = [], selectedDate, onSelectDate }) {
  const [year, monthNumber] = String(month || "").split("-").map(Number);
  const firstDay = year && monthNumber ? new Date(year, monthNumber - 1, 1) : new Date();
  const leading = mondayIndex(firstDay.getDay());
  const cells = [...Array(leading).fill(null), ...days];
  while (cells.length % 7) cells.push(null);

  return (
    <Box sx={{ ...surfaceSx, p: 1.35 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gap: .45, mb: .45 }}>
        {WEEKDAYS.map((label) => <Typography key={label} align="center" sx={{ color: "text.secondary", fontWeight: 900, fontSize: ".66rem", py: .45 }}>{label}</Typography>)}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gap: .45 }}>
        {cells.map((day, index) => {
          if (!day) return <Box key={`empty-${index}`} sx={{ minHeight: 78 }} />;
          const selected = day.date === selectedDate;
          const summary = cellCurrencySummary(day);
          const number = Number(day.date.slice(-2));
          return (
            <Box
              key={day.date}
              role="button"
              tabIndex={0}
              onClick={() => onSelectDate?.(day.date)}
              onKeyDown={(event) => { if (event.key === "Enter") onSelectDate?.(day.date); }}
              sx={{
                minHeight: 78,
                borderRadius: 2.2,
                p: .75,
                cursor: "pointer",
                border: "1px solid",
                borderColor: selected ? "primary.main" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.06)",
                bgcolor: selected ? "rgba(var(--wp-primary-rgb,25,118,210),.08)" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.018)",
                transition: "120ms ease",
                "&:hover": { borderColor: selected ? "primary.main" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.18)", transform: "translateY(-1px)" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 1000, fontSize: ".77rem" }}>{number}</Typography>
                {day.openCommitmentCount ? <Chip size="small" label={day.openCommitmentCount} sx={{ height: 18, minWidth: 24, fontSize: ".58rem", fontWeight: 950 }} /> : null}
              </Stack>
              <Stack spacing={.2} sx={{ mt: .55 }}>
                {day.routineCount ? <Typography noWrap sx={{ color: "text.secondary", fontWeight: 800, fontSize: ".58rem" }}>{day.routineCount} rutina{day.routineCount === 1 ? "" : "s"}</Typography> : null}
                {day.recurringCount ? <Typography noWrap sx={{ color: "warning.main", fontWeight: 850, fontSize: ".58rem" }}>{day.recurringCount} mensual{day.recurringCount === 1 ? "" : "es"}</Typography> : null}
                {summary ? <Typography noWrap sx={{ color: summary.net < 0 ? "error.main" : "success.main", fontWeight: 950, fontSize: ".59rem" }}>{money(summary.net, summary.currency)}{summary.extra ? ` +${summary.extra}` : ""}</Typography> : null}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
